import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { MessageCircle, Crown, Infinity as InfinityIcon } from "lucide-react";

const PREMIUM = ["rare", "epic", "legendary"];

const thumb = (u) => (u && u.startsWith("/api/files/") ? `${u}?w=420` : u);

const Face = ({ c, idx }) => (
  <Link
    to={`/companions/${c.companion_id}`}
    data-testid={`wall-face-${c.companion_id}`}
    style={{ animationDelay: `${Math.min(idx, 20) * 40}ms` }}
    className="group relative block rounded-2xl overflow-hidden border border-white/10 bg-black aspect-[3/4] animate-in fade-in zoom-in-95 duration-500 fill-mode-both hover:border-amber-400/80 hover:shadow-[0_0_30px_-6px_rgba(255,31,180,0.8)] transition-[border-color,box-shadow]"
  >
    <img src={thumb(c.generated_portrait || c.image_url)} alt={c.name} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/10 to-transparent" />
    <div className="absolute top-2 left-2 flex items-center gap-1.5 rounded-full bg-black/60 backdrop-blur-md px-2 py-0.5 text-[10px] text-sky-300 font-mono uppercase tracking-wider">
      <span className="h-1.5 w-1.5 rounded-full bg-sky-400 shadow-[0_0_8px_#22e8ff] animate-pulse" /> online
    </div>
    {PREMIUM.includes(c.rarity) && (
      <div data-testid={`wall-premium-${c.companion_id}`} className="absolute top-2 right-2 rounded-full bg-amber-500/90 text-black px-2 py-0.5 text-[10px] font-bold uppercase flex items-center gap-1"><Crown className="h-3 w-3" /> {c.rarity}</div>
    )}
    <div className="absolute bottom-0 inset-x-0 p-3">
      <div className="font-serif text-base sm:text-lg font-bold text-white leading-tight truncate">{c.name}</div>
      <div className="text-[11px] text-amber-200/90 truncate">{c.trope}</div>
      <div className="mt-2 h-8 rounded-full bg-amber-500 text-black text-xs font-bold flex items-center justify-center gap-1 md:opacity-0 md:translate-y-2 md:group-hover:opacity-100 md:group-hover:translate-y-0 transition-[opacity,transform] duration-300">
        <MessageCircle className="h-3.5 w-3.5" /> Text {c.name.split(" ")[0]}
      </div>
    </div>
  </Link>
);

export const CharacterWall = () => {
  const [all, setAll] = useState(null);
  const [cat, setCat] = useState("All");
  useEffect(() => { api.get("/companions").then((r) => setAll(r.data)).catch(() => setAll([])); }, []);
  const cats = useMemo(() => ["All", ...new Set((all || []).map((c) => c.category).filter(Boolean))], [all]);
  const shown = (all || []).filter((c) => cat === "All" || c.category === cat);

  return (
    <section id="companions" data-testid="character-wall" className="max-w-[1600px] mx-auto px-3 sm:px-6 pt-8 md:pt-12 pb-16">
      <div className="grid lg:grid-cols-[1.1fr_1fr] gap-6 items-end mb-8">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-sky-400/40 bg-sky-400/10 px-3 py-1 mb-4 font-mono text-[11px] uppercase tracking-[0.22em] text-sky-300">
            <InfinityIcon className="h-3.5 w-3.5" /> Unlimited messages · $0 per text
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[0.95]">
            Text them all night.<br /><span className="neon-text text-amber-400">It's free.</span>
          </h1>
        </div>
        <p className="text-slate-300 text-base md:text-lg max-w-xl lg:justify-self-end">
          Unlimited AI character messaging with no pay-per-message fees. Tap a face and start talking — only pay when you want more: voice, steamy mode, clips or premium characters.
        </p>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-3 mb-4 -mx-3 px-3 sm:mx-0 sm:px-0">
        {cats.map((g) => (
          <button key={g} data-testid={`wall-cat-${g}`} onClick={() => setCat(g)}
            className={`shrink-0 rounded-full h-9 px-4 text-xs font-mono uppercase tracking-widest border transition-colors ${cat === g ? "bg-amber-500 text-black border-amber-500" : "border-white/15 text-slate-300 hover:border-amber-400/60"}`}>{g}</button>
        ))}
      </div>
      {all === null ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-3">
          {Array.from({ length: 12 }).map((_, i) => <div key={i} className="aspect-[3/4] rounded-2xl bg-white/5 animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-3">
          {shown.map((c, i) => <Face key={c.companion_id} c={c} idx={i} />)}
        </div>
      )}
    </section>
  );
};
